import React, { ReactNode, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { Box, useMediaQuery, useTheme } from '@mui/material'

import { useGlobalContext } from '@/context/GlobalContext'
import Sidebar from '../Sidebar'
import OrganisationSidebar from '../Sidebar/OrganisationSidebar'
import Header from '../Header/Header'

interface MainLayoutProps {
  showSidebar: boolean
  children: ReactNode
  showHeader?: boolean
}

const MainLayout: React.FC<MainLayoutProps> = React.memo(
  ({ showSidebar, children, showHeader = true }) => {
    const { viewMode } = useGlobalContext()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [isSidebarShown, setSidebarShown] = useState(false)
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
      return (
        localStorage.getItem('isCollapsed') === 'true' ||
        viewMode === 'embedded'
      )
    })
    const location = useLocation()

    useEffect(() => {
      localStorage.setItem('isCollapsed', JSON.stringify(isCollapsed))
    }, [isCollapsed])

    // Close mobile drawer when route changes
    useEffect(() => {
      if (isMobile) {
        setSidebarShown(false)
      }
    }, [location.pathname, isMobile])

    const handleMobileDrawerToggle = () => {
      setSidebarShown(!isSidebarShown)
    }

    const handleMobileDrawerClose = () => {
      setSidebarShown(false)
    }

    return (
      <Box sx={{ display: 'flex', position: 'relative', height: '100vh' }}>
        {showSidebar && (
          <>
            {location.pathname &&
            location.pathname.startsWith('/organisation') ? (
              <OrganisationSidebar
                isCollapsed={isCollapsed}
                toggleCollapse={() => setIsCollapsed(!isCollapsed)}
                isMobile={isMobile}
                mobileOpen={isSidebarShown}
                onMobileClose={handleMobileDrawerClose}
              />
            ) : (
              <Sidebar
                isCollapsed={isCollapsed}
                toggleCollapse={() => setIsCollapsed(!isCollapsed)}
                isMobile={isMobile}
                mobileOpen={isSidebarShown}
                onMobileClose={handleMobileDrawerClose}
              />
            )}
          </>
        )}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: {
              xs: '100%',
              md: showSidebar
                ? `calc(100% - ${isCollapsed ? '70px' : '200px'})`
                : '100%',
            },
            ml: {
              xs: 0,
              md: showSidebar ? (isCollapsed ? '70px' : '200px') : 0,
            },
            transition: theme =>
              theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
          }}
        >
          {showHeader && <Header onToggleSidebar={handleMobileDrawerToggle} />}
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>{children}</Box>
        </Box>
      </Box>
    )
  },
)

export default MainLayout
